import { useEffect } from 'react';
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Card } from '@/core/ui/components';
import { useToast } from '@/app/providers';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { AuditLogFilters } from '../components/AuditLogFilters';
import { AuditLogTable } from '../components/AuditLogTable';

export function AuditPage() {
  const { showToast } = useToast();
  const {
    logs,
    isLoading,
    error,
    filters,
    setFilters,
    refresh,
    actors,
    total,
    totalPages,
    pagination,
    setPagination,
  } = useAuditLogs();

  useEffect(() => {
    if (error) showToast('error', error);
  }, [error, showToast]);

  // Cambiar un filtro con la página 3 puesta devolvería una página vacía: el
  // conjunto encogió. Se vuelve a la 1 siempre.
  const handleFiltersChange = (next: typeof filters) => {
    setFilters(next);
    setPagination({ ...pagination, page: 1 });
  };

  const startRecord = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord = total === 0 ? 0 : Math.min(pagination.page * pagination.limit, total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90 flex items-center gap-3">
            <ScrollText className="w-7 h-7" />
            Audit Log
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Platform actions by Hommi staff and security events (master DB)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <AuditLogFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          actors={actors}
        />
      </Card>

      {/* Log Table */}
      <AuditLogTable logs={logs} isLoading={isLoading} actors={actors} />

      {/* Paginación */}
      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {total === 0
              ? '0 records'
              : `Showing ${startRecord} to ${endRecord} of ${total} records`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1 || isLoading}
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              leftIcon={<ChevronLeft className="w-4 h-4" />}
            >
              Prev
            </Button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= totalPages || isLoading}
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              rightIcon={<ChevronRight className="w-4 h-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
